import defaultsDeep from "lodash.defaultsdeep";
import { get, writable } from "svelte/store";
import { locale } from "svelte-i18n";

const store = writable();

/**
 * Create a readonly configuration store from per-experience
 * component configurations. Allows components to be configured via
 * data.
 *
 * Example experience.json:
 *
 * {
 *   ...
 *   components: [
 *     {
 *       type: 'start-button',
 *       configuration: {
 *         text: 'Holla at yer boy!'
 *       }
 *     },
 *     ...
 *   ]
 * }
 *
 * @param {Array} componentConfigurations Per-experience component configs
 * @returns {object} store value
 */
export default function createStore(componentConfigurations = []) {
  if (typeof get(store) !== "undefined") {
    return get(store);
  }

  const configurations = {};

  componentConfigurations.forEach((component) => {
    configurations[component.type] = component;
  });

  store.set(configurations);

  return get(store);
}

/**
 * Get the configuration object for a given component.
 * If the component has no configuration, return the default
 * configuration object. If the component has a configuration,
 * mix in the default configuration.
 *
 * @param {string} type
 * @param {object} defaultConfiguration Default configuration
 * @returns {object}
 */
export function getConfiguration(type, defaultConfiguration) {
  if (typeof type !== "string") {
    throw new Error("Expected a `type` string");
  }

  if (typeof defaultConfiguration === "undefined") {
    throw new Error("Expected a default value to be defined");
  }

  const component = get(store)[type];

  if (component) {
    const currentLocale = get(locale) || "en";
    if (component.configuration && component.configuration[currentLocale]) {
      return defaultsDeep(
        {},
        component.configuration[currentLocale],
        defaultConfiguration
      );
    }
    return defaultsDeep({}, component.configuration, defaultConfiguration);
  }

  return defaultConfiguration;
}
